import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { cleanNotifications, showNotification } from "@mantine/notifications";
import { ApiKey } from "src/graphql/generated/graphql";
import { createAPIKeyMutation, deleteAPIKeyMutation } from "src/graphql/operations/api/mutations";
import { apiKeysQuery } from "src/graphql/operations/api/query";
import { graphqlFetch } from "src/graphql/utils/graphqlFetch";

import { handleError } from "../utils/error";
import { useAuthContext } from "./auth";

export const useAPIKeys = () => {
    const [apiKeys, setAPIKeys] = useState<ApiKey[]>([]);
    const [isLoadingAPIKeys, setIsLoadingAPIKeys] = useState(false);

    const { client } = useAuthContext();

    const fetchAPIKeys = useCallback(async (clientId: string) => {
        setIsLoadingAPIKeys(true);

        try {
            const response = await graphqlFetch(apiKeysQuery, { clientId });

            setAPIKeys(response.client?.api?.apiKeys || []);
        } catch (err) {
            handleError(err, { silent: true, additionalMessage: "Failed to load API keys" });
        } finally {
            setIsLoadingAPIKeys(false);
        }
    }, []);

    useEffect(() => {
        if (!client?.id) {
            return;
        }

        setAPIKeys([]);
        fetchAPIKeys(client?.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client?.id]);

    return {
        apiKeys,
        isLoadingAPIKeys,
        reloadAPIKeys() {
            setAPIKeys([]);
            fetchAPIKeys(client?.id);
        },
    };
};

export const useCreateAPIKey = () => {
    const { t } = useTranslation(["common"]);

    const [isCreatingAPIKey, setIsCreatingAPIKey] = useState(false);

    const { client } = useAuthContext();

    const createAPIKey = useCallback(
        async (name: string, expiresAt?: number) => {
            setIsCreatingAPIKey(true);
            showNotification({
                color: "blue",
                message: t("api_key_creating"),
                loading: true,
            });

            try {
                const response = await graphqlFetch(createAPIKeyMutation, {
                    clientId: client?.id,
                    name,
                    expiresAt,
                });

                cleanNotifications();
                showNotification({
                    color: "green",
                    message: t("api_key_creating_success"),
                });

                return response.createAPIKey;
            } catch (err) {
                handleError(err, {
                    message: { key: "api_key_creating_failed", namespace: "common" },
                });
            } finally {
                setIsCreatingAPIKey(false);
            }
        },
        [client?.id, t]
    );

    return {
        isCreatingAPIKey,
        createAPIKey,
    };
};

export const useDeleteAPIKey = () => {
    const { t } = useTranslation(["common"]);

    const [isDeletingAPIKey, setIsDeletingAPIKey] = useState(false);

    const { client } = useAuthContext();

    const deleteAPIKey = useCallback(
        async (apiKeyId: string) => {
            setIsDeletingAPIKey(true);
            showNotification({
                color: "blue",
                message: t("api_key_deleting"),
                loading: true,
            });

            try {
                const response = await graphqlFetch(deleteAPIKeyMutation, {
                    clientId: client?.id,
                    apiKeyId,
                });

                cleanNotifications();
                showNotification({
                    color: "green",
                    message: t("api_key_deleting_success"),
                });

                return response.deleteAPIKey;
            } catch (err) {
                handleError(err, {
                    message: { key: "api_key_deleting_failed", namespace: "common" },
                });
            } finally {
                setIsDeletingAPIKey(false);
            }
        },
        [client?.id, t]
    );

    return {
        isDeletingAPIKey,
        deleteAPIKey,
    };
};
